import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Download, ExternalLink, Search, TrendingUp, X } from 'lucide-react';
import type { OrderRow } from '@contracts';
import { ODOO_SB_WEB_URL, ODOO_WEB_URL } from '@/lib/config';
import { exportExcel } from '@/lib/exportData';
import { canOpenOdooLinks } from '@/services/authService';

const money = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 2 });
const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const fmtDay = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const fmtLong = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

/** Deep-link a la orden en el Odoo de VG/Pielé (acceso rápido al backoffice). */
const orderUrl = (id: number, source?: 'odoo' | 'sb') =>
  `${source === 'sb' ? ODOO_SB_WEB_URL : ODOO_WEB_URL}/web#id=${id}&model=pos.order&view_type=form`;

const commTotal = (o: OrderRow) => o.commissions.reduce((s, c) => s + c.amount, 0);
const prodTotal = (o: OrderRow) => (o.products ?? []).reduce((s, p) => s + p.subtotal, 0);
const payTotal = (o: OrderRow) => (o.payments ?? []).reduce((s, p) => s + p.amount, 0);
/** La orden se expande si hay CUALQUIER detalle: productos, comisionistas o pagos. */
const hasDetail = (o: OrderRow) =>
  o.commissions.length > 0 || (o.products?.length ?? 0) > 0 || (o.payments?.length ?? 0) > 0;

/** Estilo de fila/tarjeta según la regla maestra (devuelta / devolución / apartado / $0).
 *  Compartido por la tabla (desktop) y la tarjeta (móvil) para que no diverjan. */
function orderRowMeta(o: OrderRow, isCommission: boolean) {
  const refunded = !isCommission && o.wasRefunded;
  const accent = refunded
    ? 'border-l-rose-400 bg-rose-50/60'
    : o.isRefund
      ? 'border-l-amber-400 bg-amber-50/60'
      : o.isLayaway
        ? 'border-l-indigo-400 bg-indigo-50/50'
        : o.isZero
          ? 'border-l-slate-300 bg-slate-50'
          : 'border-l-emerald-400 hover:bg-slate-50';
  const tag = refunded
    ? { label: `Devuelta ${fmtDay(o.refundedAt)}`, cls: 'bg-rose-100 text-rose-700' }
    : o.isRefund
      ? { label: `Devolución${o.refundOfDate ? ` · de ${fmtDay(o.refundOfDate)}` : ''}`, cls: 'bg-amber-100 text-amber-700' }
      : o.isLayaway
        ? { label: 'Apartado', cls: 'bg-indigo-100 text-indigo-700' }
        : o.isZero
          ? { label: 'Garantía / $0', cls: 'bg-slate-200 text-slate-600' }
          : null;
  return { refunded, accent, tag };
}

/** Folio/referencia de la orden; deep-link a Odoo solo si el tier lo permite (gerente ve texto). */
function OrderRefLink({ o, showOdooLink }: { o: OrderRow; showOdooLink: boolean }) {
  if (!showOdooLink)
    return <span className="font-mono text-xs font-semibold text-slate-700">{o.reference || o.folio}</span>;
  return (
    <a
      href={orderUrl(o.id, o.source)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={o.source === 'sb' ? 'Abrir en Odoo SmartBamboo' : 'Abrir en Odoo'}
      className={`group inline-flex items-center gap-1 font-mono text-xs font-semibold hover:underline ${
        o.source === 'sb' ? 'text-blue-700 hover:text-blue-900' : 'text-red-700 hover:text-red-900'
      }`}
    >
      {o.reference || o.folio}
      <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
    </a>
  );
}

/** Las 3 secciones del detalle (Productos · Comisionistas · Métodos de pago).
 *  Compartido por el dropdown de la tabla (desktop) y la tarjeta (móvil): un solo markup. */
function OrderDetail({ o }: { o: OrderRow }) {
  return (
    <div className="grid gap-2 md:grid-cols-3">
      {/* ── Información general (SOLO móvil: en desktop va como columnas de la tabla) ── */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white md:hidden">
        <div className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
          Información general
        </div>
        <div className="divide-y divide-slate-50 text-xs">
          {[
            { k: 'Sucursal', v: o.posName || '—' },
            { k: 'Fecha', v: fmtDate(o.date) },
            { k: 'Cliente', v: o.customerName || '—' },
            { k: 'Vendedor', v: o.sellerName || '—' },
          ].map((f) => (
            <div key={f.k} className="flex items-center justify-between gap-3 px-3 py-1.5">
              <span className="shrink-0 text-slate-400">{f.k}</span>
              <span className="truncate text-right font-medium text-slate-700">{f.v}</span>
            </div>
          ))}
        </div>
      </div>
      {/* ── Productos ── */}
      {(o.products?.length ?? 0) > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            Productos ({o.products!.length}) · {money(prodTotal(o))}
          </div>
          {o.products!.map((p, idx) => (
            <div key={idx} className="flex items-center gap-2 border-b border-slate-50 px-3 py-1.5 text-xs last:border-0">
              <span className="shrink-0 font-semibold tabular-nums text-slate-400">{p.qty}×</span>
              <span className="truncate font-medium text-slate-700" title={p.name}>{p.name}</span>
              <span className="ml-auto shrink-0 font-bold tabular-nums text-slate-600">{money(p.subtotal)}</span>
            </div>
          ))}
        </div>
      )}
      {/* ── Comisionistas ── */}
      {o.commissions.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            Comisionistas ({o.commissions.length}) · {money(commTotal(o))}
          </div>
          {o.commissions.map((c, idx) => (
            <div key={idx} className="flex items-center gap-3 border-b border-slate-50 px-3 py-1.5 text-xs last:border-0">
              <span className="font-medium text-slate-700">{c.employeeName}</span>
              {c.pct != null && <span className="text-slate-400">{Math.round(c.pct * 100)}%</span>}
              <span className="ml-auto font-bold tabular-nums text-amber-700">{money(c.amount)}</span>
            </div>
          ))}
        </div>
      )}
      {/* ── Métodos de pago ── */}
      {(o.payments?.length ?? 0) > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            Métodos de pago ({o.payments!.length}) · {money(payTotal(o))}
          </div>
          {o.payments!.map((p, idx) => (
            <div key={idx} className="flex items-center gap-2 border-b border-slate-50 px-3 py-1.5 text-xs last:border-0">
              <span className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${p.category === 'cash' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
              <span className="font-medium text-slate-700">{p.method}</span>
              <span className="ml-auto font-bold tabular-nums text-slate-600">{money(p.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type SortCol = 'folio' | 'date' | 'pos' | 'customer' | 'seller' | 'comm' | 'total';
type SortDir = 'asc' | 'desc';
const PAGE_OPTS: { v: number; label: string }[] = [
  { v: 10, label: '10' },
  { v: 50, label: '50' },
  { v: 100, label: '100' },
  { v: 0, label: 'Todos' },
];

export interface OrdersDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  /** Título (ej. "Total de Ventas", o el nombre del vendedor/plaza). */
  title: string;
  /** Subtítulo opcional (contexto extra; las stats se calculan de las órdenes). */
  subtitle?: string;
  orders: OrderRow[];
  loading?: boolean;
  /** Muestra columna Comisión (drill desde un vendedor). */
  showCommission?: boolean;
  /** KPI desde el que se abrió: el header lidera con esa métrica. */
  metricKind?: 'sales' | 'orders' | 'avgTicket';
}

/** Drill-down de órdenes: el front solo PINTA + filtra/ordena/pagina en memoria. */
export function OrdersDrilldownModal({
  open,
  onClose,
  title,
  subtitle,
  orders,
  loading,
  showCommission,
  metricKind = 'sales',
}: OrdersDrilldownModalProps) {
  const [q, setQ] = useState('');
  const [sortCol, setSortCol] = useState<SortCol>('total');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [pos, setPos] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => setPage(0), [q, pos, sortCol, sortDir, pageSize, orders]);
  useEffect(() => setExpanded(new Set()), [orders]);

  const toggleRow = (id: number) =>
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const posNames = useMemo(
    () => Array.from(new Set(orders.map((o) => o.posName).filter(Boolean))).sort(),
    [orders],
  );

  // Modo comisión (drill desde Vendedor/Sucursal en vista "Por Vendedor"): el monto que
  // cuenta es la comisión acreditada (Σ comisionistas), no el total de la orden.
  // Solo admin/regional pueden abrir el registro en Odoo; el gerente de tienda ve el folio sin link.
  const showOdooLink = canOpenOdooLinks();
  const isCommission = !!showCommission;
  const amt = (o: OrderRow) => (isCommission ? commTotal(o) : o.total);
  // Movimiento "real" (cuadra con el KPI). Regla maestra: una venta devuelta SALE del neto
  // —tanto la línea de devolución (`isRefund`) como la venta original (`wasRefunded`)— y el $0.
  // Aplica igual en órdenes y en comisión: si la venta se devolvió, su comisión tampoco cuenta,
  // por eso el neto baja del bruto (la diferencia = las devueltas).
  const isReal = (o: OrderRow) =>
    isCommission
      ? !o.isRefund && !o.wasRefunded && !o.isZero && amt(o) > 0
      : !o.isRefund && !o.wasRefunded && !o.isZero;

  // Stats del header (sobre TODAS las órdenes): netas + ticket promedio + rango.
  const headerStats = useMemo(() => {
    const real = orders.filter(isReal);
    const net = real.reduce((s, o) => s + amt(o), 0);
    const dates = orders.map((o) => o.date).filter(Boolean).sort();
    return {
      net,
      count: real.length,
      ticket: real.length ? net / real.length : 0,
      from: dates[0],
      to: dates[dates.length - 1],
    };
  }, [orders]);

  const toggleSort = (col: SortCol) => {
    if (col === sortCol) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let rows = pos ? orders.filter((o) => o.posName === pos) : orders;
    if (term) {
      rows = rows.filter((o) =>
        [o.folio, o.reference, o.customerName, o.sellerName, o.posName]
          .some((f) => f?.toLowerCase().includes(term)),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    const str = (a: string, b: string) => a.localeCompare(b) * dir;
    const num = (a: number, b: number) => (a - b) * dir;
    const sorted = [...rows];
    sorted.sort((a, b) => {
      switch (sortCol) {
        case 'date': return str(a.date, b.date);
        case 'total': return num(a.total, b.total);
        case 'comm': return num(commTotal(a), commTotal(b));
        case 'pos': return str(a.posName, b.posName);
        case 'customer': return str(a.customerName, b.customerName);
        case 'seller': return str(a.sellerName, b.sellerName);
        default: return str(a.reference || a.folio, b.reference || b.folio);
      }
    });
    return sorted;
  }, [orders, q, pos, sortCol, sortDir]);

  // Netas = reales (cuadra con el KPI); brutas = TODO movimiento positivo que ocurrió.
  // Criterio `amt > 0` (no `!isRefund`): en VG la devolución es total NEGATIVO → fuera del
  // bruto; en SB la devolución es una COPIA positiva → SÍ entra al bruto (historial) y la
  // resta "Devueltas" la baja al neto. `amt` = comisión o total según el modo.
  const real = useMemo(() => filtered.filter(isReal), [filtered]);
  const netTotal = useMemo(() => real.reduce((s, o) => s + amt(o), 0), [real]);
  const grossRows = useMemo(() => filtered.filter((o) => amt(o) > 0 && !o.isZero), [filtered]);
  const grossTotal = useMemo(() => grossRows.reduce((s, o) => s + amt(o), 0), [grossRows]);
  const pages = pageSize ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;
  const start = pageSize ? page * pageSize : 0;
  const rows = pageSize ? filtered.slice(start, start + pageSize) : filtered;

  const doExport = () => {
    const estado = (o: OrderRow) =>
      o.isRefund ? 'Devolución' : o.wasRefunded ? 'Devuelta' : o.isZero ? 'Garantía/$0' : 'Venta';
    void exportExcel(
      (title || 'ordenes').replace(/\s+/g, '_').toLowerCase(),
      filtered.map((o) => ({
        Orden: o.reference || o.folio,
        Folio: o.folio,
        Fecha: o.date,
        Sucursal: o.posName,
        Cliente: o.customerName,
        Vendedor: o.sellerName,
        ...(showCommission ? { Comisión: commTotal(o) } : {}),
        Total: o.total,
        Estado: estado(o),
      })),
    );
  };

  if (!open) return null;

  // El header lidera con la métrica del KPI desde el que se abrió; el badge muestra la complementaria.
  const nOrd = headerStats.count.toLocaleString('es-MX');
  const lead =
    metricKind === 'orders'
      ? { primary: `${nOrd} órdenes`, secondary: `${money(headerStats.net)} netas` }
      : metricKind === 'avgTicket'
        ? { primary: money(headerStats.ticket), secondary: `${nOrd} órdenes` }
        : { primary: money(headerStats.net), secondary: `${nOrd} órdenes` };
  const badge =
    metricKind === 'avgTicket'
      ? { label: 'Transacciones', value: nOrd }
      : { label: 'Ticket promedio', value: money(headerStats.ticket) };

  const arrow = (col: SortCol) => (sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : '');
  const cols: { col: SortCol; label: string; right: boolean }[] = [
    { col: 'folio', label: 'Orden', right: false },
    { col: 'date', label: 'Fecha', right: false },
    { col: 'pos', label: 'Sucursal', right: false },
    { col: 'customer', label: 'Cliente', right: false },
    { col: 'seller', label: 'Vendedor', right: false },
    ...(showCommission ? [{ col: 'comm' as SortCol, label: 'Comisionado', right: true }] : []),
    { col: 'total', label: isCommission ? 'Total orden' : 'Total', right: true },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90dvh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header con stats + Ticket Promedio ─────────────────────────── */}
        <div className="flex items-center gap-4 bg-gradient-to-r from-[#5c1212] via-[#7d1818] to-[#a52121] px-5 py-4 text-white">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-white/25 bg-white/15">
            <span className="text-2xl font-black">$</span>
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-xl font-black leading-tight">{title}</h2>
            <p className="mt-0.5 truncate text-sm text-white/80">
              <span className="font-semibold text-white">{lead.primary}</span>
              {' · '}{lead.secondary}
              {headerStats.from && <> {' · '}{fmtLong(headerStats.from)} — {fmtLong(headerStats.to)}</>}
              {subtitle && !headerStats.from ? ` · ${subtitle}` : ''}
            </p>
          </div>
          <div className="hidden shrink-0 items-center gap-3 border-l border-white/20 pl-4 sm:flex">
            <TrendingUp className="h-7 w-7 text-white/70" />
            <div className="text-right">
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/70">{badge.label}</p>
              <p className="text-xl font-black leading-none">{badge.value}</p>
            </div>
          </div>
          <button onClick={onClose} title="Cerrar" className="shrink-0 rounded-lg p-1.5 text-white/80 hover:bg-white/15">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ── Toolbar ─────────────────────────────────────────────────────── */}
        {/* Móvil: apilado (Buscar arriba, Sucursal, y Mostrar↔Excel en una fila). Desktop
            (sm+): una sola fila; `sm:contents` disuelve el grupo Mostrar+Excel para que
            fluyan como en el diseño original. */}
        <div className="flex flex-col gap-2.5 border-b border-slate-100 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2 sm:px-5">
          {posNames.length > 1 && (
            <div className="flex items-center gap-2 sm:gap-1.5">
              <span className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-slate-400">Sucursal</span>
              <select
                value={pos}
                onChange={(e) => setPos(e.target.value)}
                className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-red-400 sm:flex-none"
              >
                <option value="">Todas</option>
                {posNames.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          )}
          <div className="relative order-first w-full sm:order-none sm:min-w-[200px] sm:flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar orden, cliente, vendedor, sucursal…"
              className="w-full rounded-xl border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-red-400"
            />
          </div>
          <div className="flex items-center justify-between gap-2 sm:contents">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Mostrar</span>
              <div className="flex gap-1">
                {PAGE_OPTS.map((p) => (
                  <button
                    key={p.v}
                    onClick={() => setPageSize(p.v)}
                    className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                      pageSize === p.v ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={doExport}
              title="Descargar Excel"
              className="flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-emerald-400 hover:text-emerald-600"
            >
              <Download className="h-4 w-4" /> Excel
            </button>
          </div>
        </div>

        {/* ── Tabla ───────────────────────────────────────────────────────── */}
        <div className="min-h-0 flex-1 overflow-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-400">Cargando órdenes…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">Sin órdenes para los filtros actuales.</div>
          ) : (
            <>
            {/* Desktop (≥ md): tabla. En móvil las 7 columnas no caben → se usa la lista de tarjetas. */}
            <table className="hidden w-full text-sm md:table">
              <thead className="sticky top-0 z-10 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500 shadow-sm">
                <tr>
                  {cols.map((h) => (
                    <th
                      key={h.col}
                      onClick={() => toggleSort(h.col)}
                      className={`cursor-pointer select-none px-4 py-2.5 font-semibold hover:text-red-600 ${h.right ? 'text-right' : ''}`}
                      title="Ordenar"
                    >
                      {h.label}
                      <span className="ml-0.5 inline-block w-2 text-red-600">{arrow(h.col)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => {
                  const { refunded, accent, tag } = orderRowMeta(o, isCommission);
                  const canOpen = hasDetail(o);
                  const isOpen = expanded.has(o.id);
                  return [
                    <tr
                      key={o.id}
                      onClick={canOpen ? () => toggleRow(o.id) : undefined}
                      title={canOpen ? 'Ver detalle' : undefined}
                      className={`border-l-4 border-b border-slate-100 ${accent} ${canOpen ? 'cursor-pointer' : ''} ${isOpen ? 'bg-slate-100/70' : ''}`}
                    >
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1">
                          <ChevronRight
                            className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''} ${canOpen ? '' : 'invisible'}`}
                          />
                          <OrderRefLink o={o} showOdooLink={showOdooLink} />
                        </span>
                        {tag && (
                          <span className={`ml-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${tag.cls}`}>
                            {tag.label}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-slate-500">{fmtDate(o.date)}</td>
                      <td className="px-4 py-2.5">{o.posName}</td>
                      <td className="px-4 py-2.5">{o.customerName}</td>
                      <td className="px-4 py-2.5">{o.sellerName || '—'}</td>
                      {showCommission && (
                        <td className="px-4 py-2.5 text-right text-slate-600">
                          {o.commissions.length ? money(commTotal(o)) : '—'}
                        </td>
                      )}
                      <td
                        className={`px-4 py-2.5 text-right font-bold tabular-nums ${
                          o.isRefund ? 'text-rose-600' : refunded ? 'text-slate-400 line-through' : 'text-emerald-700'
                        }`}
                      >
                        {money(o.total)}
                      </td>
                    </tr>,
                    isOpen && canOpen ? (
                      <tr key={`${o.id}-detail`} className="bg-slate-50">
                        <td colSpan={cols.length} className="px-4 py-2">
                          <OrderDetail o={o} />
                        </td>
                      </tr>
                    ) : null,
                  ];
                })}
              </tbody>
            </table>

            {/* Móvil (< md): tarjetas apiladas — sin scroll horizontal. Tap = acordeón con las 3 secciones. */}
            <ul className="divide-y divide-slate-100 md:hidden">
              {rows.map((o) => {
                const { refunded, accent, tag } = orderRowMeta(o, isCommission);
                const canOpen = hasDetail(o);
                const isOpen = expanded.has(o.id);
                return (
                  <li key={o.id} className={`border-l-4 ${accent}`}>
                    <div
                      onClick={canOpen ? () => toggleRow(o.id) : undefined}
                      className={`flex items-center gap-2 px-3 py-3.5 ${canOpen ? 'cursor-pointer' : ''}`}
                    >
                      <ChevronRight
                        className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''} ${canOpen ? '' : 'invisible'}`}
                      />
                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-1">
                        <OrderRefLink o={o} showOdooLink={showOdooLink} />
                        {tag && (
                          <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${tag.cls}`}>
                            {tag.label}
                          </span>
                        )}
                      </div>
                      <div
                        className={`shrink-0 font-bold tabular-nums ${
                          o.isRefund ? 'text-rose-600' : refunded ? 'text-slate-400 line-through' : 'text-emerald-700'
                        }`}
                      >
                        {money(o.total)}
                      </div>
                    </div>
                    {isOpen && canOpen && (
                      <div className="px-3 pb-3">
                        <OrderDetail o={o} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            </>
          )}
        </div>

        {/* ── Footer: brutas/comisión − Devueltas = Netas + paginación ──────── */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 bg-slate-50/60 px-5 py-3 text-sm">
          <span className="text-slate-500">
            {isCommission ? 'Comisión bruta' : 'Ventas brutas'} ({grossRows.length}):{' '}
            <span className="font-semibold text-slate-700">{money(grossTotal)}</span>
          </span>
          {grossTotal - netTotal > 0 && (
            <span className="text-rose-500">
              − Devueltas: <span className="font-semibold">{money(grossTotal - netTotal)}</span>
            </span>
          )}
          <span className="text-slate-600">
            = {isCommission ? 'Comisión neta' : 'Ventas netas'} ({real.length}):{' '}
            <span className="font-bold text-emerald-700">{money(netTotal)}</span>
          </span>
          <div className="ml-auto flex items-center gap-3 text-slate-500">
            <span className="text-xs">
              {pageSize ? `${start + 1}–${Math.min(start + pageSize, filtered.length)} de ${filtered.length}` : `${filtered.length} órdenes`}
            </span>
            {pageSize > 0 && pages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="rounded-lg px-2 py-1 hover:bg-slate-200 disabled:opacity-40"
                >
                  ‹
                </button>
                <span className="text-xs">{page + 1} / {pages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
                  disabled={page >= pages - 1}
                  className="rounded-lg px-2 py-1 hover:bg-slate-200 disabled:opacity-40"
                >
                  ›
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
